import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CountdownTimer } from '@/ui/CountdownTimer';

export function HomePage() {
  useEffect(() => {
    document.title = 'ESDFL MUN 2025 | Edirne Süleyman Demirel Fen Lisesi Model United Nations';
  }, []);

  return (
    <div>
      <Helmet>
        <title>ESDFL MUN 2025 | Edirne Süleyman Demirel Fen Lisesi Model United Nations</title>
        <meta name="description" content="ESDFL MUN 2025 - 6-7 Aralık 2025 tarihlerinde Edirne Süleyman Demirel Fen Lisesi'nde düzenlenecek Model Birleşmiş Milletler konferansı. Etkinlik programı, galeri ve duyurular için resmi web sitesi." />
        <link rel="canonical" href="https://mun.esdfl.net/" />
      </Helmet>
      
      <div className="flex justify-center mb-6">
        <h1 className="text-2xl md:text-3xl font-serif font-bold text-center text-gray-900 dark:text-white bg-white/85 dark:bg-gray-800/85 backdrop-blur-sm py-3 px-8 rounded-xl border border-gray-200/50 dark:border-gray-700/50">Hoş Geldiniz</h1>
      </div>
      
      {/* Countdown Section */}
      <div className="card p-8 mb-8 bg-white/85 dark:bg-gray-800/85">
        <div className="text-center mb-6">
          <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-white mb-1">
            ESDFLMUN'25
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">6-7 Aralık 2025 - Etkinliğe Kalan Süre</p>
        </div>
        <CountdownTimer />
      </div>

      <div className="card p-8 mb-8 dark:bg-gray-800/85">
        <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-center">
          Edirne Süleyman Demirel Fen Lisesi Model United Nations (MUN) etkinliğinin web sayfasına hoş geldiniz.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Link to="/takvim" className="card p-6 hover:shadow-md transition group dark:bg-gray-800/85">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 group-hover:bg-brand-100 dark:group-hover:bg-brand-900/50 transition">
              <CalendarIcon className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Etkinlik Takvimi</h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Yaklaşan konferanslar ve etkinlikler</p>
        </Link>
        <Link to="/galeri" className="card p-6 hover:shadow-md transition group dark:bg-gray-800/85">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 group-hover:bg-brand-100 dark:group-hover:bg-brand-900/50 transition">
              <PhotoIcon className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Galeri</h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Etkinliklerimizden fotoğraflar</p>
        </Link>
        <Link to="/duyurular" className="card p-6 hover:shadow-md transition group dark:bg-gray-800/85">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-brand-50 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400 group-hover:bg-brand-100 dark:group-hover:bg-brand-900/50 transition">
              <MegaphoneIcon className="w-5 h-5" />
            </div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Duyurular</h2>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Güncel haberler ve duyurular</p>
        </Link>
      </div>
    </div>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
    </svg>
  );
}

function PhotoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" />
    </svg>
  );
}

function MegaphoneIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.34 15.84c-.688-.06-1.386-.09-2.09-.09H7.5a4.5 4.5 0 110-9h.75c.704 0 1.402-.03 2.09-.09m0 9.18c.253.962.584 1.892.985 2.783.247.55.06 1.21-.463 1.511l-.657.38c-.551.318-1.26.117-1.527-.461a20.845 20.845 0 01-1.44-4.282m3.102.069a18.03 18.03 0 01-.59-4.59c0-1.586.205-3.124.59-4.59m0 9.18a23.848 23.848 0 018.835 2.535M10.34 6.66a23.847 23.847 0 008.835-2.535m0 0A23.74 23.74 0 0018.795 3m.38 1.125a23.91 23.91 0 011.014 5.395m-1.014 8.855c-.118.38-.245.754-.38 1.125m.38-1.125a23.91 23.91 0 001.014-5.395m0-3.46c.495.413.811 1.035.811 1.73 0 .695-.316 1.317-.811 1.73m0-3.46a24.347 24.347 0 010 3.46" />
    </svg>
  );
}
